import { ScreeningSurveyAttributesSchema } from "@clipboard-health/contract-home-health-api";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { useCallback } from "react";
import { z } from "zod";

import { type ScreeningSurveyFormData } from "../lib/screeningSurveySchema";

// Bump when the persisted shape or the survey structure changes in a way that
// makes older saved state unsafe to restore. Old entries fall back to DEFAULT_STATE.
const PERSISTED_STATE_VERSION = 1;

const persistedSurveyStateSchema = z.object({
  version: z.literal(PERSISTED_STATE_VERSION),
  stepIndex: z.number().int().nonnegative(),
  formValues: ScreeningSurveyAttributesSchema.partial(),
});

type PersistedSurveyState = z.infer<typeof persistedSurveyStateSchema>;

const DEFAULT_STATE: PersistedSurveyState = {
  version: PERSISTED_STATE_VERSION,
  stepIndex: 0,
  formValues: {},
};

function deserializePersistedState(raw: string): PersistedSurveyState {
  try {
    const result = persistedSurveyStateSchema.safeParse(JSON.parse(raw));
    return result.success ? result.data : DEFAULT_STATE;
  } catch {
    return DEFAULT_STATE;
  }
}

const LOCAL_STORAGE_OPTIONS = {
  deserializer: deserializePersistedState,
};

function getStorageKey(workerId: string) {
  return `screening-survey-progress-${workerId}`;
}

export function useScreeningSurveyPersistedState(workerId: string) {
  const [persistedState, setPersistedState, clearPersistedState] =
    useLocalStorage<PersistedSurveyState>(
      getStorageKey(workerId),
      DEFAULT_STATE,
      LOCAL_STORAGE_OPTIONS
    );

  const persistStepIndex = useCallback(
    (stepIndex: number) => {
      setPersistedState((previous) => ({ ...previous, stepIndex }));
    },
    [setPersistedState]
  );

  const persistFormValues = useCallback(
    (formValues: Partial<ScreeningSurveyFormData>) => {
      setPersistedState((previous) => ({ ...previous, formValues }));
    },
    [setPersistedState]
  );

  return {
    persistedState,
    persistStepIndex,
    persistFormValues,
    clearPersistedState,
  };
}
